/* eslint-disable @typescript-eslint/no-shadow */
import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {AppState} from '@store';
import {CtsDistributionApiService} from '@api/cts-distribution/cts-distribution.api.service';
import {concatMap, debounceTime, map, switchMap, withLatestFrom} from 'rxjs/operators';
import {mapActionsFn, ToastService} from 'amc-common-components';
import {ctsDistributionActions} from '@store/cts-distribution/actions';
import {EditModalContainer} from '@pages/cts-distribution/edit-modal/edit-modal.container';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {ctsDistributionSelectors} from '@store/cts-distribution/selectors';
import {DEFAULT_ACTIONS_DEBOUNCE_MS} from '@shared/constants/debounce.constants';
import {CTSDistributionColumns, CTSDistributionDateFilter} from '@entities/cts-distribution/enums';
import {API_DATE_FORMAT, CODE_DATE} from '@shared/constants/formats.constants';
import {CTS_TABLE_INITIAL_FILTERS} from '@entities/cts-distribution/constants';

/**
 * This class is an injectable Angular service that provides a way to fetch data for Rights Page.
 */
@Injectable()
export class CtsDistributionEffects {
	/**
	 * The dialog reference for the EditModalContainer.
	 */
	private editModal: MatDialogRef<EditModalContainer>;

	/**
	 * This function creates an Angular effect that get table data.
	 * @returns an Observable that emits data when the effect completes
	 */
	getTableData$ = createEffect(() =>
		this.actions$.pipe(
			ofType(ctsDistributionActions.getTableData.requested),
			withLatestFrom(
				this.store.select(ctsDistributionSelectors.table.filters),
				this.store.select(ctsDistributionSelectors.table.sorting),
				this.store.select(ctsDistributionSelectors.pagination.currentPage),
				this.store.select(ctsDistributionSelectors.historicalDate)
			),
			switchMap(([, filters, sorting, pageNum, historicalDate]) =>
				this.apiService
					.getTableData({
						...filters,
						SORT_BY: sorting.column,
						SORT_ROUTE: sorting.order,
						AS_OF_DATE: historicalDate?.format(API_DATE_FORMAT) ?? null,
						PAGE_NUM: pageNum,
						ROWS_PER_PAGE: 300,
					})
					.pipe(mapActionsFn(ctsDistributionActions.getTableData))
			)
		)
	);

	/**
	 * This function creates an Angular effect that updates pages count.
	 * @returns an Observable that emits data when the effect completes
	 */
	updatePagesCount$ = createEffect(() =>
		this.actions$.pipe(
			ofType(ctsDistributionActions.getTableData.succeeded),
			map(({payload}) =>
				ctsDistributionActions.getPagesCount.succeeded({
					payload: payload.pagination.totalPages,
				})
			)
		)
	);

	/**
	 * This function creates an Angular effect that get platform filter.
	 * @returns an Observable that emits data when the effect completes
	 */
	getPlatformFilter$ = createEffect(() =>
		this.actions$.pipe(
			ofType(ctsDistributionActions.getPlatformFilter.requested),
			switchMap(() =>
				this.apiService.getPlatformFilter().pipe(mapActionsFn(ctsDistributionActions.getPlatformFilter))
			)
		)
	);

	/**
	 * This function creates an Angular effect that get period filter.
	 * @returns an Observable that emits data when the effect completes
	 */
	getPeriodFilter$ = createEffect(() =>
		this.actions$.pipe(
			ofType(ctsDistributionActions.getPeriodFilter.requested),
			switchMap(() =>
				this.apiService.getPeriodFilter().pipe(mapActionsFn(ctsDistributionActions.getPeriodFilter))
			)
		)
	);

	/**
	 * This function creates an Angular effect that get program type filter.
	 * @returns an Observable that emits data when the effect completes
	 */
	getProgramTypeFilter$ = createEffect(() =>
		this.actions$.pipe(
			ofType(ctsDistributionActions.getProgramTypeFilter.requested),
			switchMap(() =>
				this.apiService.getProgramTypeFilter().pipe(mapActionsFn(ctsDistributionActions.getProgramTypeFilter))
			)
		)
	);

	/**
	 * This function creates an Angular effect that get period filter.
	 * @returns an Observable that emits data when the effect completes
	 */
	updateTableFilters$ = createEffect(() =>
		this.actions$.pipe(
			ofType(ctsDistributionActions.getPeriodFilter.succeeded),
			withLatestFrom(this.store.select(ctsDistributionSelectors.periodFilter.data)),
			map(([, period]) =>
				ctsDistributionActions.setTableFilters({
					payload: {
						...CTS_TABLE_INITIAL_FILTERS,
						[CTSDistributionDateFilter.dateFrom]: +period.minDate.format(CODE_DATE),
						[CTSDistributionDateFilter.dateTo]: +period.maxDate.format(CODE_DATE),
					},
				})
			)
		)
	);

	/**
	 * This function creates an Angular effect that save rows.
	 * @returns an Observable that emits data when the effect completes
	 */
	saveRows$ = createEffect(() =>
		this.actions$.pipe(
			ofType(ctsDistributionActions.saveRows.requested),
			concatMap(({payload}) =>
				this.apiService.saveRows(payload).pipe(
					map((result) => {
						if (!result) {
							return;
						}

						this.editModal?.close();

						return {
							status: result[1][0],
							data: result[0].map((row) => ({
								...row,
								editedCells: Object.entries(payload)
									.filter(([, value]) => value !== null && !Array.isArray(value))
									.map(([key]) => key),
							})),
						};
					}),
					mapActionsFn(ctsDistributionActions.saveRows)
				)
			)
		)
	);

	/**
	 * This function creates an Angular effect that save rows.
	 * @returns an Observable that emits data when the effect completes
	 */
	updateRows$ = createEffect(() =>
		this.actions$.pipe(
			ofType(ctsDistributionActions.saveRows.succeeded),
			withLatestFrom(this.store.select(ctsDistributionSelectors.table.data)),
			concatMap(([{payload}, rows]) => {
				const editedRows = rows.map((row) => ({
					...row,
				}));

				payload.data.forEach((savedRow) => {
					const editedRowIdx = rows.findIndex(
						(row) =>
							row[CTSDistributionColumns.Platform] === savedRow[CTSDistributionColumns.Platform] &&
							row[CTSDistributionColumns.MediaAmcnId] === savedRow[CTSDistributionColumns.MediaAmcnId]
					);

					savedRow.editedCells.forEach((colId) => {
						const newValue = savedRow[colId];

						editedRows[editedRowIdx] = {
							...editedRows[editedRowIdx],
							[colId]: newValue,
							editedCells: [...(rows[editedRowIdx]?.editedCells ?? []), colId],
						};
					});
				});

				if (!payload.status.STATUS_ID) {
					ToastService.error({
						message: payload.status.MESSAGE,
					});
				} else {
					ToastService.info({
						message: payload.status.MESSAGE,
					});
				}

				return [ctsDistributionActions.updateTableRows({payload: editedRows})];
			})
		)
	);

	/**
	 * This function creates an Angular effect that reset selected page.
	 * @returns an Observable that emits data when the effect completes
	 */
	resetSelectedPage$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				ctsDistributionActions.setTableFilters,
				ctsDistributionActions.setTableSorting,
				ctsDistributionActions.setHistoricalDate
			),
			debounceTime(DEFAULT_ACTIONS_DEBOUNCE_MS),
			map(() => ctsDistributionActions.selectPage({payload: 1}))
		)
	);

	/**
	 * This function creates an Angular effect that updates table rows.
	 * @returns an Observable that emits data when the effect completes
	 */
	updateTableRows$ = createEffect(() =>
		this.actions$.pipe(
			ofType(ctsDistributionActions.selectPage),
			debounceTime(DEFAULT_ACTIONS_DEBOUNCE_MS),
			map(() => ctsDistributionActions.getTableData.requested())
		)
	);

	/**
	 * This function creates an Angular effect that open edit modal.
	 * @returns an Observable that emits data when the effect completes
	 */
	protected readonly openEditModal$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(ctsDistributionActions.openEditModal),
				map(({payload}) => {
					this.editModal = this.dialog.open(EditModalContainer, {
						autoFocus: false,
						data: payload,
					});
				})
			),
		{dispatch: false}
	);

	/**
	 * Constructor for CtsDistributionEffects class.
	 * @constructor
	 * @param {Actions} actions$ actions
	 * @param {CtsDistributionApiService} apiService cts distribution api service
	 * @param {Store<AppState>} store store
	 * @param {MatDialog} dialog dialog
	 */
	constructor(
		private actions$: Actions,
		private apiService: CtsDistributionApiService,
		private store: Store<AppState>,
		private dialog: MatDialog
	) {}
}
